import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { api, sendAsync } from "../../../app/applicationSlice"
import { RootState, RootThunk, RootThunkApi } from "../../../app/store"
import { LoadingState } from "../../../models/common"
import {
  NatureTokenValue,
  NatureValue,
  patientInfo,
  PreScriptionIframeValue,
} from "../../../models/intelligence"
import * as R from "../../../models/registration"
import { Registration } from "../../../models/registration"
import * as Tr from "../../../models/treatment"
import { Treatment } from "../../../models/treatment"
import {
  getIframePreScription,
  getNature,
  getNatureToken,
  NatureBody,
  PreScriptBody,
  setNature,
} from "../../../services/intelligence"
import { startTreatment as apiStartTreatment } from "../../../services/registration"
import {
  copyTreatment as apiCopyTreatment,
  CopyTreatmentParams,
  createTreatment as apiCreateTreatment,
  getTreatment as apiGetTreatment,
  GetTreatmentDetailParams,
  getUrlLink,
  urlLinkParams,
} from "../../../services/treatment"
import { setTreatmentRegistration } from "../treatmentSlice"

interface DiagnosisState {
  registration?: Registration
  treatment?: Treatment
  creatingState: LoadingState
}

const initialState = {
  registration: undefined,
  creatingState: LoadingState.Idle,
} as DiagnosisState

export const createTreatment = createAsyncThunk<void, Treatment, RootThunkApi>(
  "diagnosis/createTreatmentStatus",
  async (treatment, api) => {
    return sendAsync(apiCreateTreatment(treatment), api).then(() => {
      // const id = api.getState().diagnosis.registration?.id;
      // if (id) {
      //   api.dispatch(getTreatment({ registrationId: id,medicalRecord:1 }));
      // }
    })
  }
)

const diagnosisSlice = createSlice({
  name: "diagnosis",
  initialState,
  reducers: {
    setRegistration: (state, action: PayloadAction<Registration>) => {
      state.registration = action.payload
    },
    setTreatment: (state, action: PayloadAction<Treatment | undefined>) => {
      state.treatment = action.payload
    },
    setTreatmentCreatingState: (state, action: PayloadAction<LoadingState>) => {
      state.creatingState = action.payload
    },
    resetDiagnosis: (state) => {
      state.registration = undefined
      state.treatment = undefined
      state.creatingState = LoadingState.Idle
    },
  },
})

const { setRegistration } = diagnosisSlice.actions

export const {
  setTreatmentCreatingState,
  resetDiagnosis,
  setTreatment,
} = diagnosisSlice.actions

export function getTreatment(params: GetTreatmentDetailParams): RootThunk {
  return api(
    apiGetTreatment(params),
    (data: Record<string, never>, dispatch) => {
      const registration = R.fromJson(data.registration)
      dispatch(setRegistration(registration))
      dispatch(setTreatmentRegistration(registration))
      const treatment =
        JSON.stringify(data.treatment) !== "{}"
          ? Tr.fromJson(data.treatment)
          : undefined
      dispatch(setTreatment(treatment))
    }
  )
}

export const getTreatments = createAsyncThunk<
  patientInfo,
  GetTreatmentDetailParams,
  RootThunkApi<void>
>("diagnosis/getTreatments", async (params, api) => {
  return sendAsync(apiGetTreatment(params), api)
})

export function startTreatment(registrationId: string): RootThunk {
  return api(apiStartTreatment(registrationId), () => {
    // Do nothing.
  })
}

export const copyTreatment = createAsyncThunk<
  void,
  CopyTreatmentParams,
  RootThunkApi<void>
>("diagnosis/copyTreatmentStatus", async (params, api) => {
  return sendAsync(apiCopyTreatment(params), api).then(() => {
    // do nothing.
  })
})

export const getUrl = createAsyncThunk<void, urlLinkParams, RootThunkApi>(
  "diagnosis/getUrl",
  async (params, api) => {
    return sendAsync(getUrlLink(params), api)
  }
)

export const getNatureAsync = createAsyncThunk<
  NatureValue,
  string,
  RootThunkApi<void>
>("diagonsis/getNature", async (code, api) => {
  return sendAsync(getNature(code), api)
})

export const setNatureAsync = createAsyncThunk<
  any,
  NatureBody,
  RootThunkApi<void>
>("diagonsis/setNature", async (body, api) => {
  return sendAsync(setNature(body), api)
})

export const getNatureTokenAsync = createAsyncThunk<
  NatureTokenValue,
  void,
  RootThunkApi<void>
>("diagonsis/getNatureToken", async (_, api) => {
  return sendAsync(getNatureToken(), api)
})

export const getIframePreScriptionAsync = createAsyncThunk<
  PreScriptionIframeValue,
  PreScriptBody,
  RootThunkApi<void>
>("diagonsis/getIframe", async (body, api) => {
  return sendAsync(getIframePreScription(body), api)
})

export const selectRegistration = (
  state: RootState
): Registration | undefined => state.diagnosis.registration

export const selectTreatment = (state: RootState): Treatment | undefined =>
  state.diagnosis.treatment

export const selectTreatmentCreatingState = (state: RootState): LoadingState =>
  state.diagnosis.creatingState

export default diagnosisSlice.reducer
